import { NgTemplateOutlet } from '@angular/common';
import { booleanAttribute, ChangeDetectionStrategy, Component, computed, contentChild, input, model, output, TemplateRef } from '@angular/core';
import { HisCoreSharedModule } from '../../../core/his-core-shared.module';
import { intersection, isBlank, isNonEmptyArray, isString } from '../../../core/utils';
import { HisTableContextDirective } from '../../directive/his-table-context.directive';
import { TableColumnConfig, TableConfig } from '../../model/table-model';
import { HisTableConfigComponent } from '../config/his-table-config.component';

const TABLE_CONFIG_OPTIONS = ['frozenCols', 'paginator', 'reorderableColumns'] as const;
type TableConfigOptions = ( typeof TABLE_CONFIG_OPTIONS )[number];

function hasOption( options: TableConfigOptions[] | false, option: TableConfigOptions ): boolean {
    return options !== false && options.includes( option );
}

function parseTableConfigInput( val: TableConfigOptions[] | string | boolean ): TableConfigOptions[] | false {
    if ( isString( val ) ) {
        if ( !isBlank( val ) ) {
            return intersection( val.split( ',' ).map( v => v.trim() as TableConfigOptions ), [...TABLE_CONFIG_OPTIONS] );
        }
    }
    else if ( typeof val === 'boolean' ) {
        if ( val === false ) {
            return false;
        }
    }
    else if ( isNonEmptyArray( val ) ) {
        return intersection( val, [...TABLE_CONFIG_OPTIONS] );
    }
    return ['frozenCols', 'paginator']; // default
}

@Component( {
    selector: 'his-table-toolbar',
    standalone: true,
    imports: [HisTableConfigComponent, HisTableContextDirective, HisCoreSharedModule, NgTemplateOutlet],
    templateUrl: './his-table-toolbar.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
} )
export class HisTableToolbarComponent {

    /** Zeigt die Filter-Buttons an. */
    public readonly showTableFilter = input( false, { transform: booleanAttribute } );

    /** Zeigt den Button zum Zurücksetzen des Filters an. */
    public readonly showResetFilter = input( false, { transform: booleanAttribute } );

    /** Sichtbarkeitszustand des Filters. */
    public readonly toggleTableFilter = model( false );

    /** Event: Filter zurücksetzen. */
    public readonly resetFilter = output<void>();

    /** Zeigt die Auf-/Zuklappen-Buttons an (nur Treetable). */
    public readonly showExpandCollapse = input( false, { transform: booleanAttribute } );

    /** Event: Baum vollständig aufklappen. */
    public readonly expandAll = output<void>();

    /** Event: Baum vollständig zuklappen. */
    public readonly collapseAll = output<void>();

    /**
     * Zeigt den Bereich "Tabelle anpassen" an. Kann entweder als simples Flag, oder als Array spezifischer Tabellenkonfigurationsoptionen verwendet werden.\
     * Über diese Eingabe kann gesteuert werden, welche Optionen im Dialog "Tabelle anpassen" angezeigt werden.
     * ```html
     * // Example usage as a simple flag
     * <his-table-toolbar showTableConfig></his-table-toolbar>
     * <!-- Shows 'frozenColumns' and 'paginator' options by default -->
     *
     * // Example usage with specific table configuration options
     * <his-table-toolbar [showTableConfig]="['reorderableColumns', 'paginator']"></his-table-toolbar>
     * <!-- Shows 'reorderableColumns' and 'paginator' options -->
     * ```
     * Wird `false` übergeben, wird der Bereich "Tabelle anpassen" vollständig ausgeblendet.\
     * Statt eines Arrays kann auch ein kommaseparierter String verwendet werden, dieser bietet jedoch keine Typunterstützung.
     */
    public readonly showTableConfig = input<TableConfigOptions[] | false, TableConfigOptions[] | string | boolean>( false, { transform: parseTableConfigInput } );
    protected readonly enableReorderableColumns = computed( () => hasOption( this.showTableConfig(), 'reorderableColumns' ) );
    protected readonly enableFrozenColumns = computed( () => hasOption( this.showTableConfig(), 'frozenCols' ) );
    protected readonly enablePaginator = computed( () => hasOption( this.showTableConfig(), 'paginator' ) );
    /** Spaltenkonfiguration der Tabelle. */
    public readonly columns = input<TableColumnConfig[]>( [] );
    /** Eindeutige ID der Tabelle. */
    public readonly tableId = input<string>();
    /** Event: Tabellenkonfiguration gespeichert. */
    public readonly tableConfigSaved = output<TableConfig>();
    /** Custom table configuration template for more control over the component. Replaces the default implementation. */
    protected readonly customTableConfig = contentChild<TemplateRef<void>>( 'customTableConfig' );

    /** Custom Buttons links */
    protected readonly customButtonsLeft = contentChild<TemplateRef<void>>( 'customButtonsLeft' );

    /** Custom Buttons rechts vor den Standard-Buttons */
    protected readonly customButtonsRightBefore = contentChild<TemplateRef<void>>( 'customButtonsRightBefore' );

    /** Custom Buttons rechts nach den Standard-Buttons */
    protected readonly customButtonsRightAfter = contentChild<TemplateRef<void>>( 'customButtonsRightAfter' );
}
